// No dependencies: the dispatcher and site handler read these addresses too.

const segment = "[A-Za-z0-9_-]{1,128}";
// A member's link token, or a demo link's signed sample listener (api/src/podcasts/demo.ts).
const tokenSegment = "[A-Za-z0-9_-]{43}|demo/[0-9a-f-]{36}/[0-9a-f]{32}";

/**
 * A creator's podcast addresses on their own site. Every one ends in a file name with a dot,
 * which no page path can (shared/src/pages.ts), so they never collide with a creator's pages.
 */
export const podcastPaths = {
  feed: (token?: string) => (token ? `/podcast/${token}.xml` : "/podcast.xml"),
  artwork: (imageId: string, extension: "jpg" | "png") =>
    `/podcast/artwork/${imageId}/cover.${extension}`,
  episodeArtwork: (episodeId: string, extension: "jpg" | "png") =>
    `/podcast/episodes/${episodeId}/artwork.${extension}`,
  audio: (episodeId: string, assetId: string, extension: ".mp3" | ".m4a", token?: string) =>
    `/podcast${token ? `/${token}` : ""}/episodes/${episodeId}/${assetId}/audio${extension}`,
  transcript: (episodeId: string, assetId: string, trackId: string, token?: string) =>
    `/podcast${token ? `/${token}` : ""}/episodes/${episodeId}/${assetId}/transcripts/${trackId}.vtt`,
};

export type PodcastPath =
  | { kind: "feed"; token?: string }
  | { kind: "artwork"; imageId: string; filename: string }
  | { kind: "episodeArtwork"; episodeId: string; filename: string }
  | { kind: "audio"; episodeId: string; assetId: string; filename: string; token?: string }
  | {
      kind: "transcript";
      episodeId: string;
      assetId: string;
      trackId: string;
      token?: string;
    };

const pathPatterns: [RegExp, (match: RegExpExecArray) => PodcastPath][] = [
  [/^\/podcast\.xml$/, () => ({ kind: "feed" })],
  [new RegExp(`^/podcast/(${tokenSegment})\\.xml$`), (m) => ({ kind: "feed", token: m[1]! })],
  [
    new RegExp(`^/podcast/artwork/(${segment})/(cover\\.(?:jpg|png))$`),
    (m) => ({ kind: "artwork", imageId: m[1]!, filename: m[2]! }),
  ],
  [
    new RegExp(`^/podcast/episodes/(${segment})/(artwork\\.(?:jpg|png))$`),
    (m) => ({ kind: "episodeArtwork", episodeId: m[1]!, filename: m[2]! }),
  ],
  [
    new RegExp(
      `^/podcast(?:/(${tokenSegment}))?/episodes/(${segment})/(${segment})/(audio\\.(?:mp3|m4a))$`,
    ),
    (m) => ({
      kind: "audio",
      episodeId: m[2]!,
      assetId: m[3]!,
      filename: m[4]!,
      ...(m[1] ? { token: m[1] } : {}),
    }),
  ],
  [
    new RegExp(
      `^/podcast(?:/(${tokenSegment}))?/episodes/(${segment})/(${segment})/transcripts/(${segment})\\.vtt$`,
    ),
    (m) => ({
      kind: "transcript",
      episodeId: m[2]!,
      assetId: m[3]!,
      trackId: m[4]!,
      ...(m[1] ? { token: m[1] } : {}),
    }),
  ],
];

/** One of a creator's podcast addresses, or null for any other path. */
export function parsePodcastPath(pathname: string): PodcastPath | null {
  for (const [pattern, read] of pathPatterns) {
    const match = pattern.exec(pathname);
    if (match) return read(match);
  }
  return null;
}

/**
 * Opens a feed straight in a podcast app that supports adding a show by its address. Spotify
 * has no such link: it lists public feeds from its own directory and takes no private ones.
 */
export function podcastAppLinks(feedUrl: string) {
  const url = new URL(feedUrl);
  const bare = `${url.host}${url.pathname}${url.search}`;
  return [
    { app: "Apple Podcasts", href: `podcast://${bare}` },
    { app: "Overcast", href: `overcast://x-callback-url/add?url=${encodeURIComponent(url.href)}` },
    { app: "Pocket Casts", href: `pktc://subscribe/${bare}` },
  ] as const;
}
